import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getAssociates } from '../../services/associate';
import {
  Search as SearchIcon,
  RestartAlt,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsExcel, exportAsCSV } from '../../utils/importExport';

const FollowupReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    status: '',
    searchTerm: '',
  });
  const [reportData, setReportData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  useEffect(() => {
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${baseURL}/api/globalMasterDetails?type=inquiryStatus`);
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          const leadStatuses = result.data.filter(
            (item) => item.details && item.details.stageType === 'Lead'
          );
          setStatuses(leadStatuses);
        }
      } catch (error) {
        console.error('Failed to fetch inquiry statuses:', error);
      } finally {
        setLoadingStatuses(false);
      }
    };
    fetchStatuses();
  }, []);

  // Associate dropdown states with pagination
  const [associateOptions, setAssociateOptions] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateFetchingRef = useRef(false);
  const associateInitialLoadRef = useRef(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [associateHasMore, setAssociateHasMore] = useState(true);
  const associateSearchTermRef = useRef('');

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (filters.fromDate) {
        const from = new Date(filters.fromDate);
        if (!isNaN(from)) params.append('fromDate', from.toISOString().split('T')[0]);
      }
      if (filters.toDate) {
        const to = new Date(filters.toDate);
        if (!isNaN(to)) params.append('toDate', to.toISOString().split('T')[0]);
      }
      if (filters.status && filters.status !== 'All Status') params.append('status', filters.status);
      if (filters.searchTerm) params.append('searchKey', filters.searchTerm);
      if (selectedAssociate && selectedAssociate.id) params.append('associateId', selectedAssociate.id);

      const response = await fetch(`${baseURL}/api/reports/followUpReport?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();

      if (result.status && result.data && Array.isArray(result.data.docs)) {
        setReportData(result.data.docs);
        setTotalDocs(result.data.totalDocs || 0);
      } else {
        setReportData([]);
        setTotalDocs(0);
      }
    } catch (error) {
      console.error('Error fetching followup report:', error);
      setReportData([]);
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, selectedAssociate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchReportData();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchReportData]);



  // Fetch associates for autocomplete with pagination
  const fetchAssociates = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (associateFetchingRef.current) return;
    if (!append && !associateHasMore && pageNum > 1) return;

    associateFetchingRef.current = true;
    setLoadingAssociates(true);
    try {
      const params = {
        page: pageNum,
        limit: 20,
      };

      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await getAssociates(params);

      let associates = [];
      let total = 0;

      if (response.status && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (response.success && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (Array.isArray(response.data?.docs)) {
        associates = response.data.docs;
        total = parseInt(response.extra?.total || response.data.totalDocs || response.totalCount || associates.length, 10);
      }

      const formattedAssociates = associates.map((associate) => ({
        id: associate._id || associate.id,
        name: associate.name || '',
        number: String(associate.associateId || associate._id || ''),
        phone: String(associate.phone || associate.whatsapp || ''),
      }));

      if (append) {
        setAssociateOptions(prev => {
          const newOptions = [...prev, ...formattedAssociates];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
          setAssociateHasMore(hasMore);
          return newOptions;
        });
      } else {
        setAssociateOptions(formattedAssociates);
        const currentTotal = formattedAssociates.length;
        const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
        setAssociateHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociateOptions([]);
        setAssociateHasMore(false);
      }
    } finally {
      setLoadingAssociates(false);
      associateFetchingRef.current = false;
    }
  }, []);

  // Debounced associate search
  useEffect(() => {
    const trimmedInput = associateSearchInput.trim();

    if (trimmedInput.length === 0) {
      if (!associateInitialLoadRef.current) {
        associateInitialLoadRef.current = true;
        return;
      }
      return;
    }

    if (trimmedInput.length < 2) {
      return;
    }

    if (associateSearchTermRef.current !== trimmedInput) {
      setAssociatePage(1);
      setAssociateHasMore(true);
      associateSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchAssociates(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [associateSearchInput, fetchAssociates]);

  const handleReset = () => {
    setFilters({
      fromDate: null,
      toDate: null,
      status: '',
      searchTerm: '',
    });
    setSelectedAssociate(null);
    setPage(0);
    setAssociateSearchInput('');
    setAssociatePage(1);
    setAssociateHasMore(true);
    associateSearchTermRef.current = '';
    fetchAssociates('', 1, false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Build rows shared by all export formats
  const buildExportRows = () =>
    reportData.map((followup, index) => ({
      'S.No': page * rowsPerPage + index + 1,
      'Date': followup.followUpDate ? new Date(followup.followUpDate).toLocaleDateString() : '-',
      'Customer Name': followup.customer || '-',
      'Contact': followup.mobileNumber || '-',
      'Followup Type': followup.followUpType || '-',
      'Status': followup.inquiryStatus || '-',
      'Assigned To': followup.followedUpBy || '-',
      'Next Followup': followup.nextFollowUpDate ? new Date(followup.nextFollowUpDate).toLocaleDateString() : '-',
      'Remark': followup.followUpRemark || '-',
    }));

  const handleExportPdf = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsPDF({ rows, title: 'Follow-up Report', fileNameBase: 'followup_report', orientation: 'l' });
  };

  const handleExportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsCSV({ rows, fileNameBase: 'followup_report' });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsExcel({ rows, sheetName: 'Followup Report', fileNameBase: 'followup_report' });
  };


  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Follow-up Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage customer follow-ups
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
              variant="contained"
            />
            <RefreshButton
              onClick={() => fetchReportData()}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Follow-up Filters
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <DesktopDatePicker
                  label="From Date"
                  inputFormat="dd/MM/yyyy"
                  value={filters.fromDate}
                  onChange={(date) => setFilters({ ...filters, fromDate: date })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DesktopDatePicker
                  label="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={filters.toDate}
                  onChange={(date) => setFilters({ ...filters, toDate: date })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    {statuses.map((statusItem) => (
                      <MenuItem key={statusItem._id} value={statusItem.details.statusName}>
                        {statusItem.details.statusName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={associateOptions}
                  getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                  value={selectedAssociate}
                  onChange={(event, newValue) => {
                    setSelectedAssociate(newValue);
                    setPage(0);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setAssociateSearchInput(newInputValue);
                      if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                        setAssociatePage(1);
                        setAssociateHasMore(true);
                        associateSearchTermRef.current = '';
                      }
                    }
                  }}
                  onOpen={() => {
                    if (associateOptions.length === 0 && !associateFetchingRef.current) {
                      setAssociatePage(1);
                      setAssociateHasMore(true);
                      fetchAssociates(associateSearchInput || '', 1, false);
                    }
                  }}
                  loading={loadingAssociates}
                  ListboxProps={{
                    onScroll: (event) => {
                      const listboxNode = event.currentTarget;
                      const scrollTop = listboxNode.scrollTop;
                      const scrollHeight = listboxNode.scrollHeight;
                      const clientHeight = listboxNode.clientHeight;
                      const scrollBottom = scrollTop + clientHeight;

                      if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                        if (associateHasMore && !loadingAssociates && !associateFetchingRef.current) {
                          const nextPage = associatePage + 1;
                          setAssociatePage(nextPage);
                          fetchAssociates(associateSearchInput || '', nextPage, true);
                        }
                      }
                    },
                    style: { maxHeight: '300px', overflow: 'auto' },
                  }}
                  filterOptions={(options) => options}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assigned To"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Search Section */}
            <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search Follow-ups"
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  placeholder="Search by customer name, contact, type, assigned to..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<RestartAlt />}
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '40px'
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Search Results Summary */}
      {(filters.searchTerm || filters.status || selectedAssociate || filters.fromDate || filters.toDate) && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon fontSize="small" />
            <strong>Search Results:</strong> {totalDocs} follow-up{totalDocs !== 1 ? 's' : ''} found
            {filters.searchTerm && ` matching "${filters.searchTerm}"`}
            {filters.status && filters.status !== 'All Status' && ` with status "${filters.status}"`}
            {selectedAssociate && ` assigned to "${selectedAssociate.name}"`}
          </Typography>
        </Box>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Follow-up Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Phone</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Followup Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Next Followup</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Assigned To</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Remark</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : reportData.length > 0 ? (
                  reportData.map((row, index) => (
                    <TableRow
                      key={row._id || index}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.followUpDate ? new Date(row.followUpDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.customer || '-'}</TableCell>
                      <TableCell>{row.mobileNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.followUpType || '-'}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.inquiryStatus || '-'}
                          size="small"
                          color={row.inquiryStatus === 'HOT' ? 'error' : row.inquiryStatus === 'WARM' ? 'warning' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.nextFollowUpDate ? new Date(row.nextFollowUpDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{row.followedUpBy || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.followUpRemark || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No follow-ups found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalDocs > 0 && (
            <TablePagination
              
              component="div"
              count={totalDocs}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>


    </Box>
  );
};

export default FollowupReport;

